import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  CustomDropdownContainer,
  CustomDropdownTrigger,
  CustomDropdownMenu,
  CustomDropdownOption,
  OptionHeader,
  OptionLabelContainer,
  OptionLabel,
  OptionDescription,
  CheckIcon,
  OptionLegend,
  LayerDescription,
  OpacityControl,
  OpacityLabel,
  OpacitySlider,
  OpacityValue,
} from '../LayersPanel.styles'
import { Layer } from '../LayersPanel.types'
import { Icon } from '../../../atoms/Icon'
import { ExpandArrow } from '../../../atoms/ExpandArrow'
import {
  DemographicsLegend,
  EmploymentLegend,
  PricingLegend,
  NoiseLegend,
  SunlightLegend,
  SunDirectionLegend,
} from '../../AnalysisMap/components'

export interface LayersPanelExclusiveDropdownGroupProps {
  layer: Layer
  isExpanded: boolean
  onLayerSelect: (layerId: string, value: string) => void
  onOpacityChange: (layerId: string, opacity: number) => void
  isDemographicsLayer: (layerId: string) => boolean
  isEmploymentLayer: (layerId: string) => boolean
  isPricingLayer: (layerId: string) => boolean
  isNoiseLayer: (layerId: string) => boolean
  isSunlightLayer: (layerId: string) => boolean
  isSunDirectionLayer: (layerId: string) => boolean
  /** Whether to show a "No layer" option at the top of the dropdown */
  showNoLayerOption?: boolean
  /** Optional custom controls to render after opacity slider */
  renderCustomControls?: (selectedLayerId: string) => React.ReactNode
}

export const LayersPanelExclusiveDropdownGroup: React.FC<
  LayersPanelExclusiveDropdownGroupProps
> = ({
  layer,
  onLayerSelect,
  onOpacityChange,
  isDemographicsLayer,
  isEmploymentLayer,
  isPricingLayer,
  isNoiseLayer,
  isSunlightLayer,
  isSunDirectionLayer,
  showNoLayerOption = false,
  renderCustomControls,
}) => {
  const { t } = useTranslation('ui')
  const [openDropdownId, setOpenDropdownId] = React.useState<string | null>(
    null
  )
  const dropdownRef = React.useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdownId(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  if (!layer.options) return null

  const isOpen = openDropdownId === layer.id
  const NO_LAYER_VALUE = 'none'
  const isNoLayerSelected = layer.selectedValue === NO_LAYER_VALUE
  const selectedOption = isNoLayerSelected
    ? {
        value: NO_LAYER_VALUE,
        label: t('analysisMap.layersPanel.disabledSelectLayer'),
        description: undefined,
      }
    : layer.options.find((opt) => opt.value === layer.selectedValue)

  return (
    <>
      <CustomDropdownContainer ref={dropdownRef}>
        <CustomDropdownTrigger
          onClick={() => setOpenDropdownId(isOpen ? null : layer.id)}
          $isOpen={isOpen}
        >
          <span>
            {selectedOption?.label || t('analysisMap.layersPanel.selectLayer')}
          </span>
          <ExpandArrow $expanded={isOpen} direction="down" />
        </CustomDropdownTrigger>

        {isOpen && (
          <CustomDropdownMenu>
            {/* "No layer" option */}
            {showNoLayerOption && (
              <CustomDropdownOption
                key={NO_LAYER_VALUE}
                $isSelected={isNoLayerSelected}
                onClick={() => {
                  onLayerSelect(layer.id, NO_LAYER_VALUE)
                  setOpenDropdownId(null)
                }}
              >
                <OptionHeader>
                  <OptionLabelContainer>
                    <OptionLabel $isSelected={isNoLayerSelected}>
                      {t('analysisMap.layersPanel.disableLayer')}
                    </OptionLabel>
                  </OptionLabelContainer>
                  {isNoLayerSelected && (
                    <CheckIcon>
                      <Icon name="check" size="small" />
                    </CheckIcon>
                  )}
                </OptionHeader>
              </CustomDropdownOption>
            )}

            {/* Regular layer options */}
            {layer.options.map((option) => {
              const isSelected = option.value === layer.selectedValue
              const isDisabled = option.disabled === true
              return (
                <CustomDropdownOption
                  key={option.value}
                  $isSelected={isSelected}
                  $isDisabled={isDisabled}
                  onClick={() => {
                    if (!isDisabled) {
                      onLayerSelect(layer.id, option.value)
                      setOpenDropdownId(null)
                    }
                  }}
                >
                  <OptionHeader>
                    <OptionLabelContainer>
                      <OptionLabel
                        $isSelected={isSelected}
                        $isDisabled={isDisabled}
                      >
                        {option.label}
                      </OptionLabel>
                      {option.description && (
                        <OptionDescription>
                          {option.description}
                        </OptionDescription>
                      )}
                    </OptionLabelContainer>
                    {isSelected && !isDisabled && (
                      <CheckIcon>
                        <Icon name="check" size="small" />
                      </CheckIcon>
                    )}
                  </OptionHeader>
                  {isDemographicsLayer(option.value) && (
                    <OptionLegend>
                      <DemographicsLegend layerId={option.value} />
                    </OptionLegend>
                  )}
                  {isEmploymentLayer(option.value) && (
                    <OptionLegend>
                      <EmploymentLegend layerId={option.value} />
                    </OptionLegend>
                  )}
                  {isPricingLayer(option.value) && (
                    <OptionLegend>
                      <PricingLegend layerId={option.value} />
                    </OptionLegend>
                  )}
                  {isNoiseLayer(option.value) && (
                    <OptionLegend>
                      <NoiseLegend layerId={option.value} />
                    </OptionLegend>
                  )}
                  {isSunlightLayer(option.value) && (
                    <OptionLegend>
                      <SunlightLegend layerId={option.value} />
                    </OptionLegend>
                  )}
                  {isSunDirectionLayer(option.value) && (
                    <OptionLegend>
                      <SunDirectionLegend layerId={option.value} />
                    </OptionLegend>
                  )}
                </CustomDropdownOption>
              )
            })}
          </CustomDropdownMenu>
        )}
      </CustomDropdownContainer>

      {/* Show description below custom dropdown (only if not "No layer") */}
      {layer.selectedValue &&
        !isNoLayerSelected &&
        layer.options &&
        (() => {
          const selectedOpt = layer.options.find(
            (opt) => opt.value === layer.selectedValue
          )
          if (selectedOpt?.description) {
            return (
              <LayerDescription>{selectedOpt.description}</LayerDescription>
            )
          }
          return null
        })()}

      {/* Show legend for the selected option (only if not "No layer") */}
      {layer.selectedValue &&
        !isNoLayerSelected &&
        isDemographicsLayer(layer.selectedValue) && (
          <div style={{ padding: '8px 0 0 0', marginLeft: '12px' }}>
            <DemographicsLegend layerId={layer.selectedValue} />
          </div>
        )}
      {layer.selectedValue &&
        !isNoLayerSelected &&
        isEmploymentLayer(layer.selectedValue) && (
          <div style={{ padding: '8px 0 0 0', marginLeft: '12px' }}>
            <EmploymentLegend layerId={layer.selectedValue} />
          </div>
        )}
      {layer.selectedValue &&
        !isNoLayerSelected &&
        isPricingLayer(layer.selectedValue) && (
          <div style={{ padding: '8px 0 0 0', marginLeft: '12px' }}>
            <PricingLegend layerId={layer.selectedValue} />
          </div>
        )}
      {layer.selectedValue &&
        !isNoLayerSelected &&
        isNoiseLayer(layer.selectedValue) && (
          <div style={{ padding: '8px 0 0 0', marginLeft: '12px' }}>
            <NoiseLegend layerId={layer.selectedValue} />
          </div>
        )}
      {layer.selectedValue &&
        !isNoLayerSelected &&
        isSunlightLayer(layer.selectedValue) && (
          <div style={{ padding: '8px 0 0 0', marginLeft: '12px' }}>
            <SunlightLegend layerId={layer.selectedValue} />
          </div>
        )}
      {layer.selectedValue &&
        !isNoLayerSelected &&
        isSunDirectionLayer(layer.selectedValue) && (
          <div style={{ padding: '8px 0 0 0', marginLeft: '12px' }}>
            <SunDirectionLegend layerId={layer.selectedValue} />
          </div>
        )}

      {/* Show opacity slider (only if not "No layer") */}
      {layer.showOpacity && !isNoLayerSelected && (
        <OpacityControl>
          <OpacityLabel>{t('analysisMap.layersPanel.opacity')}</OpacityLabel>
          <OpacitySlider
            type="range"
            min="0"
            max="100"
            value={layer.opacity || 100}
            onChange={(e) =>
              onOpacityChange(layer.id, parseInt(e.target.value))
            }
          />
          <OpacityValue>{layer.opacity || 100}%</OpacityValue>
        </OpacityControl>
      )}

      {/* Render custom controls if provided (only if not "No layer") */}
      {!isNoLayerSelected &&
        layer.selectedValue &&
        renderCustomControls &&
        renderCustomControls(layer.selectedValue)}
    </>
  )
}
